import {
  RULE_CATEGORIES,
  type NotificationRoute,
  type NotificationRule,
  type RuleChannel,
  type RuleClause,
  type RuleDelivery,
  type RuleSeverity,
} from '@/hooks/useNotifications.ts';

/**
 * Rendering a rule as a sentence.
 *
 * Its own module because the sentence *is* the rule — it is what the list shows,
 * what the composer previews live, and the only place a user reads back what they
 * configured. That makes it worth testing directly, without mounting a component.
 */

export const CHANNEL_LABELS: Record<RuleChannel, string> = {
  in_app: 'In-app',
  email: 'Email',
  push: 'Push',
};

export const SEVERITY_LABELS: Record<RuleSeverity, string> = {
  info: 'Info',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

/**
 * Something a clause can point at.
 *
 * Tags are two kinds rather than one because the axis is not a property of the tag —
 * it is which object the clause asks about. `deviceTag` reads the tags on the devices,
 * regions and divisions an event touched; `alertTag` reads the tags on the definition
 * that raised it. The same tag can be used both ways and mean different things.
 */
export interface ScopeOption {
  id: string;
  name: string;
  kind: 'division' | 'region' | 'deviceTag' | 'alertTag';
}

export function isTagKind(kind: ScopeOption['kind']): boolean {
  return kind === 'deviceTag' || kind === 'alertTag';
}

/**
 * Split clauses into disjunction groups, preserving first-appearance order.
 *
 * Mirrors `matcher::group` on the backend, and must: the sentence a user reads back is
 * a promise about what the matcher will do, so two different groupings of the same rows
 * would make the preview lie. A clause with no `groupId` is its own group — never
 * folded in with other group-less clauses, which would silently turn an AND into an OR.
 */
export function groupClauses(clauses: RuleClause[]): { key: string; members: RuleClause[] }[] {
  const out: { key: string; members: RuleClause[] }[] = [];
  const byKey = new Map<string, { key: string; members: RuleClause[] }>();
  clauses.forEach((clause, index) => {
    if (!clause.groupId) {
      out.push({ key: `ungrouped-${String(index)}`, members: [clause] });
      return;
    }
    const existing = byKey.get(clause.groupId);
    if (existing) {
      existing.members.push(clause);
      return;
    }
    const group = { key: clause.groupId, members: [clause] };
    byKey.set(clause.groupId, group);
    out.push(group);
  });
  return out;
}

/**
 * What a clause points at, keyed the way `ScopeOption` is — so the picker can say which
 * of its entries a rule already uses, and so two conditions can be compared.
 *
 * Keyed on **kind and id**, not id alone: one tag definition can be selectable on both
 * axes, where it asks genuinely different questions (the tags on the devices an event
 * touched, versus the tags on the definition that raised it). Keying on the id alone
 * would treat those two as the same target, which they are not.
 *
 * A tag's key deliberately stops at the **definition** and ignores the value. Two
 * conditions on `environment` are the same key whether they pin `prod` or `staging` —
 * and that is the case worth catching, because "affecting devices tagged env: prod AND
 * affecting devices tagged env: staging" is what someone writes when they mean *or*.
 */
export function clauseTargetKey(clause: RuleClause): string | null {
  if (clause.tagDefinitionId) {
    return `${clause.tagScope === 'alert_definition' ? 'alertTag' : 'deviceTag'}:${clause.tagDefinitionId}`;
  }
  if (clause.divisionId) return `division:${clause.divisionId}`;
  if (clause.regionId) return `region:${clause.regionId}`;
  return null;
}

/**
 * The quantifier as the MATCHER reads it.
 *
 * On the alert-definition axis an event has at most one definition, so "affecting" and
 * "confined to" are the same question and `matcher::evaluate_group` never looks at the
 * quantifier. Comparing the stored value here would report two identical alert-tag
 * conditions as compatible purely because one chip says "confined to" — a difference
 * with no effect, used to excuse a real duplicate.
 */
function effectiveQuantifier(clause: RuleClause): string {
  return clause.tagScope === 'alert_definition' ? 'unquantified' : clause.quantifier;
}

/**
 * The exact thing a clause selects, tag **value included**.
 *
 * The counterpart to [`clauseTargetKey`], and the distinction matters more than it
 * looks. `clauseTargetKey` stops at the definition, which is right for "is this tag
 * already used?" and right for spotting a misread OR. It is wrong for deciding whether
 * two conditions cancel out: `env: Production` and `env: Development` are different
 * targets, and a rule saying "affecting one, not affecting the other" is an ordinary
 * exclusion policy — the exact tag analogue of "affecting region Ashburn and not
 * affecting region Lab", which nothing has ever flagged.
 */
function exactTargetKey(clause: RuleClause): string | null {
  const target = clauseTargetKey(clause);
  if (target === null) return null;
  return clause.tagDefinitionId ? `${target}:${clause.tagValueId ?? 'any'}` : target;
}

/**
 * The *field* a clause selects on — `region`, `division`, or one tag definition.
 *
 * Coarser than [`clauseTargetKey`]: every region shares one axis key, because "region"
 * is the field and Ashburn is a value of it. This is what makes two region conditions
 * comparable at all, and what finally makes regions and tags behave alike — a tag
 * definition is a field with values in exactly the same way.
 */
function clauseAxisKey(clause: RuleClause): string | null {
  if (clause.tagDefinitionId) {
    return `${clause.tagScope === 'alert_definition' ? 'alertTag' : 'deviceTag'}:${clause.tagDefinitionId}`;
  }
  if (clause.divisionId) return 'division';
  if (clause.regionId) return 'region';
  return null;
}

/**
 * Why two conditions were flagged.
 *
 * Three tiers, because they are three different situations and only two of them are
 * mistakes:
 *
 * - `contradiction` — **red.** The rule can never match.
 * - `duplicate` — **red.** The same condition, written twice.
 * - `overlap` — **amber.** Legal, matchable, and probably not what was meant.
 */
export type ConflictKind = 'contradiction' | 'duplicate' | 'overlap';

/** Reds first: worse things should win when a chip is caught by more than one pair. */
const SEVERITY: Record<ConflictKind, number> = { contradiction: 3, duplicate: 2, overlap: 1 };

export interface ClauseConflict {
  kind: ConflictKind;
  message: string;
  /**
   * The other group keys this one can be folded into, when folding is meaningful.
   * Empty for a contradiction: two conditions with opposite `is` cannot share a group,
   * because negation is a property of the group, so there is nothing to collapse — one
   * of them has to go, and only the author knows which.
   */
  mergeWith: string[];
}

/**
 * Conditions that name the same thing and probably should not both be there.
 *
 * Groups AND, so two conditions about one field are a spectrum rather than a single
 * mistake, and flattening that spectrum is what made the first version wrong.
 *
 * | same field, and… | same `is` | opposite `is` |
 * |---|---|---|
 * | **same target** | `duplicate` — written twice (red) | `contradiction` — can never match (red) |
 * | **different targets** | `overlap` — probably meant "or" (amber) | *nothing* — an ordinary exclusion policy |
 *
 * ⚠ **The bottom-right cell is the one that was wrong.** "Affecting devices tagged
 * env: Production AND not affecting devices tagged env: Development" was reported as a
 * rule that can never match, because the contradiction branch compared tag targets at
 * the definition level and ignored the value. It matches perfectly well — an incident on
 * a Production device touching no Development device — and it is the exact tag analogue
 * of "affecting region Ashburn and not affecting region Lab", which regions never
 * flagged, because each region is its own key.
 *
 * Detected on **overlap**, not on set equality: a condition naming {Ashburn, Dallas}
 * alongside one naming {Ashburn} has the same problem as two naming {Ashburn}, and
 * requiring identical sets would quietly miss it.
 *
 * Returns group keys, so a caller can mark exactly the chips involved.
 */
export function findClauseConflicts(groups: { key: string; members: RuleClause[] }[]): Map<string, ClauseConflict> {
  const found = new Map<string, ClauseConflict>();

  const keysOf = (members: RuleClause[], key: (c: RuleClause) => string | null) =>
    new Set(members.map(key).filter((k): k is string => k !== null));
  const intersects = (a: RuleClause[], b: RuleClause[], key: (c: RuleClause) => string | null) => {
    const left = keysOf(a, key);
    return [...keysOf(b, key)].some((k) => left.has(k));
  };

  const record = (key: string, conflict: ClauseConflict) => {
    const existing = found.get(key);
    if (!existing) {
      found.set(key, conflict);
      return;
    }
    // Keep the worst kind and its wording, but accumulate every merge target — a chip
    // caught by two pairs should still collapse both in one click.
    const worst = SEVERITY[conflict.kind] > SEVERITY[existing.kind] ? conflict : existing;
    found.set(key, {
      kind: worst.kind,
      message: worst.message,
      mergeWith: [...new Set([...existing.mergeWith, ...conflict.mergeWith])],
    });
  };

  const pair = (a: string, b: string, conflict: Omit<ClauseConflict, 'mergeWith'>, mergeable: boolean) => {
    record(a, { ...conflict, mergeWith: mergeable ? [b] : [] });
    record(b, { ...conflict, mergeWith: mergeable ? [a] : [] });
  };

  for (let i = 0; i < groups.length; i++) {
    for (let j = i + 1; j < groups.length; j++) {
      const a = groups[i];
      const b = groups[j];
      // Groups are non-empty by construction (`groupClauses` never creates an empty
      // bucket), and the quantifier and negation are group properties, so the first
      // member speaks for the group — the same read the matcher does.
      const first = a.members[0];
      const second = b.members[0];

      if (effectiveQuantifier(first) !== effectiveQuantifier(second)) continue;
      const sameReading = first.negated === second.negated;

      if (intersects(a.members, b.members, exactTargetKey)) {
        if (sameReading) {
          pair(
            a.key,
            b.key,
            { kind: 'duplicate', message: 'This condition is already here. Merge them, or remove one.' },
            true,
          );
        } else {
          pair(
            a.key,
            b.key,
            {
              kind: 'contradiction',
              message:
                'This condition and another one cancel out — conditions combine with AND, so the rule can never match. Remove one.',
            },
            false,
          );
        }
        continue;
      }

      // Opposite readings of different targets are an exclusion policy, not a mistake:
      // "affecting Production, not affecting Development". Nothing to say.
      if (!sameReading) continue;

      if (intersects(a.members, b.members, clauseAxisKey)) {
        pair(
          a.key,
          b.key,
          {
            kind: 'overlap',
            message:
              'Another condition covers the same thing. Conditions combine with AND, so an event has to satisfy both — merge them to match either.',
          },
          true,
        );
      }
    }
  }

  return found;
}

/** What one clause points at, with no quantifier or negation on the front. */
function targetPhrase(clause: RuleClause, scopes: ScopeOption[], tagValueLabels?: Record<string, string>): string {
  if (clause.tagDefinitionId) {
    const key = scopes.find((s) => s.id === clause.tagDefinitionId && isTagKind(s.kind))?.name ?? 'a tag';
    const valued = clause.tagValueId ? `${key}: ${tagValueLabels?.[clause.tagValueId] ?? '…'}` : key;
    return clause.tagScope === 'alert_definition' ? `alerts tagged ${valued}` : `devices tagged ${valued}`;
  }
  const id = clause.divisionId ?? clause.regionId;
  const noun = clause.divisionId ? 'division' : 'region';
  return `${noun} ${scopes.find((s) => s.id === id)?.name ?? 'unknown'}`;
}

/** A named tag value, or `…` when it cannot be resolved — never "any value". */
function valueLabel(clause: RuleClause, tagValueLabels?: Record<string, string>): string {
  return clause.tagValueId ? (tagValueLabels?.[clause.tagValueId] ?? '…') : 'any value';
}

/**
 * One group, read aloud: "affecting region Ashburn or Dallas", "not confined to
 * division Acme", "affecting devices tagged env: prod or staging".
 *
 * The quantifier and negation are said ONCE, on the front, because they belong to the
 * group rather than to its members — "confined to (Ashburn or Dallas)" asks one
 * question of the event's whole scope set. Saying them per member would read as two
 * conditions and describe a rule that does not exist.
 *
 * A group whose members share a noun collapses it ("region Ashburn or Dallas", not
 * "region Ashburn or region Dallas"). The general join is still there for a mixed
 * group: the matcher evaluates those correctly and the editor cannot currently build
 * one, but a sentence that cannot describe a stored rule is worse than a clumsy one.
 */
export function clauseGroupSentence(
  members: RuleClause[],
  scopes: ScopeOption[],
  tagValueLabels?: Record<string, string>,
): string {
  // Non-empty by construction: `groupClauses` never yields an empty group, and a
  // single clause reaches here as a group of one.
  const first = members[0];
  const negation = first.negated ? 'not ' : '';

  const definitionId = first.tagDefinitionId;
  const sharedTag = definitionId && members.every((m) => m.tagDefinitionId === definitionId);
  const sharedRegion = !first.tagDefinitionId && !first.divisionId && members.every((m) => !!m.regionId);
  const sharedDivision = !first.tagDefinitionId && members.every((m) => !!m.divisionId);

  let targets: string;
  if (sharedTag) {
    const key = scopes.find((s) => s.id === definitionId && isTagKind(s.kind))?.name ?? 'a tag';
    const noun = first.tagScope === 'alert_definition' ? 'alerts tagged' : 'devices tagged';
    // A group pinning no value at all is the broad "tagged with this at all" clause,
    // and appending ": any value" to it would read as narrower than it is.
    targets = members.every((m) => !m.tagValueId)
      ? `${noun} ${key}`
      : `${noun} ${key}: ${members.map((m) => valueLabel(m, tagValueLabels)).join(' or ')}`;
  } else if (sharedRegion || sharedDivision) {
    const noun = sharedDivision ? 'division' : 'region';
    const names = members.map((m) => scopes.find((s) => s.id === (m.divisionId ?? m.regionId))?.name ?? 'unknown');
    targets = `${noun} ${names.join(' or ')}`;
  } else {
    targets = members.map((m) => targetPhrase(m, scopes, tagValueLabels)).join(' or ');
  }

  // The alert-definition axis is unquantified — an event has at most one definition,
  // so "affecting" and "confined to" are the same question and the matcher does not
  // ask it (`matcher::evaluate_group`). Saying it anyway would offer a distinction
  // that does not exist.
  if (first.tagScope === 'alert_definition') return `${negation}from ${targets}`;

  return `${negation}${first.quantifier === 'all' ? 'confined to' : 'affecting'} ${targets}`;
}

/**
 * One clause, read aloud: "affecting region Ashburn", "not confined to division Acme".
 *
 * The four quantifier/negation combinations (§3.1.1) each say something different
 * about an incident that spans regions, and this is where that difference becomes
 * legible — the chip's two toggles are only usable because the result reads as
 * English.
 *
 * A group of one, deliberately: one reader for both means a single clause and a
 * one-member group can never disagree about how they read.
 */
export function clauseSentence(clause: RuleClause, scopes: ScopeOption[], tagValueLabels?: Record<string, string>): string {
  return clauseGroupSentence([clause], scopes, tagValueLabels);
}

const DELIVERY_PHRASES: Record<Exclude<RuleDelivery, 'immediate'>, string> = {
  hourly_digest: 'hourly digest',
  daily_digest: 'daily digest',
  weekly_digest: 'weekly digest',
};

/**
 * The category / severity / clause spine, shared by rules (§3.1) and routes (§3.2).
 *
 * They match on identical fields — only the ends differ, a channel and a delivery
 * mode on one side, a destination on the other. Reading them the same way is the
 * point: an admin who understands one understands the other.
 */
function matchSentence(
  match: {
    categories: string[] | null;
    categoriesNegated: boolean;
    minSeverity: RuleSeverity;
    maxSeverity: RuleSeverity | null;
    includeResolutions: boolean;
    clauses: RuleClause[];
  },
  scopes: ScopeOption[],
  tagValueLabels?: Record<string, string>,
): string[] {
  const categories =
    match.categories === null
      ? 'Everything'
      : match.categories.map((c) => RULE_CATEGORIES.find((rc) => rc.key === c)?.label ?? c).join(', ');
  const categoryPart =
    match.categories === null ? categories : `${match.categoriesNegated ? 'Everything except ' : ''}${categories}`;

  const severity = match.maxSeverity
    ? `${SEVERITY_LABELS[match.minSeverity]}–${SEVERITY_LABELS[match.maxSeverity]}`
    : `${SEVERITY_LABELS[match.minSeverity]}+`;

  const parts = [categoryPart, severity];
  for (const { members } of groupClauses(match.clauses)) parts.push(clauseGroupSentence(members, scopes, tagValueLabels));
  if (!match.includeResolutions) parts.push('opens only');
  return parts;
}

/**
 * The whole rule as one flat sentence — no nesting, because there is none to show.
 *
 * `destinationName` names the address an email rule was pointed at. Passed in rather
 * than read off the rule because the rules API returns `destinationId` and not a
 * label — deliberately, since a rule is readable by its owner alone and the id is all
 * the server needs. Omitted or null reads as the account's own email, which is what
 * the router resolves when a rule names no destination.
 */
export function ruleSentence(
  rule: NotificationRule,
  scopes: ScopeOption[],
  destinationName?: string | null,
  tagValueLabels?: Record<string, string>,
): string {
  const channel = destinationName ? `${CHANNEL_LABELS[rule.channel]} → ${destinationName}` : CHANNEL_LABELS[rule.channel];
  const parts = [channel, ...matchSentence(rule, scopes, tagValueLabels)];
  // §3.1's template ends "{immediately | as a digest}". Immediate is the overwhelming
  // default and saying so on every rule would be noise, so only a digest announces
  // itself — the sentence is how a user notices they asked to wait a week.
  if (rule.delivery !== 'immediate') parts.push(DELIVERY_PHRASES[rule.delivery]);
  return parts.join(' · ');
}

/**
 * A route as one sentence — the same grammar as a rule, ending in where it goes.
 *
 * A route names a destination instead of a channel, and that is the whole difference
 * a reader needs: a rule says "send *me*", a route says "send *there*". The
 * destination's display name is used because the API deliberately does not return its
 * address to route readers, who are a wider set than destination managers.
 */
export function routeSentence(
  route: NotificationRoute,
  scopes: ScopeOption[],
  tagValueLabels?: Record<string, string>,
): string {
  const parts = matchSentence(route, scopes, tagValueLabels);
  parts.push(`→ ${route.destinationName ?? 'no destination'}`);
  return parts.join(' · ');
}
